/**
 * Custom hook to manage bank form state
 * Provides typed state management and change handlers
 */

import { useState, useCallback } from "react";
import { NO_REGEX, SWIFT_CODE_REGEX } from "@funded-here-interface/common";
import { BankFormErrors } from "../utils/validation";

export interface BankFormState {
  bankName: string;
  bankCode: string;
  accountNumber: string;
  accountType: string;
  swiftCode: string;
  accountHolderName: string;
  bankAddress: string;
  bankCountry: string;
  currency: string;
}

/**
 * Hook to manage bank form state and field updates
 * @param initialState - Initial form state
 * @returns Form state, setters, and change handlers
 */
export const useBankFormState = (initialState: BankFormState) => {
  const [formState, setFormState] = useState<BankFormState>(initialState);
  const [errors, setErrors] = useState<BankFormErrors>({
    bankName: "",
    accountHolderName: "",
    bankAddress: "",
    swiftCode: "",
    accountNumber: "",
    accountType: "",
    bankCountry: "",
    currency: "",
  });

  /**
   * Clear error for a specific field
   */
  const clearError = useCallback((field: keyof BankFormErrors) => {
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  /**
   * Set all errors
   */
  const setAllErrors = useCallback((newErrors: BankFormErrors) => {
    setErrors(newErrors);
  }, []);

  /**
   * Update a single form field
   */
  const updateField = useCallback(
    (field: keyof BankFormState, value: string) => {
      setFormState((prev) => ({ ...prev, [field]: value }));
      clearError(field as keyof BankFormErrors);
    },
    [clearError]
  );

  /**
   * Create change handler for text fields
   */
  const createChangeHandler = useCallback(
    (field: keyof BankFormState) => (value: string) => {
      updateField(field, value);
    },
    [updateField]
  );

  /**
   * Create change handler for numeric fields
   */
  const createNumericChangeHandler = useCallback(
    (field: keyof BankFormState) => (value: string) => {
      const numericValue = value.replace(NO_REGEX, "");
      updateField(field, numericValue);
    },
    [updateField]
  );

  /**
   * Create change handler for SWIFT code (alphanumeric only)
   */
  const createSwiftCodeChangeHandler = useCallback(
    (field: keyof BankFormState) => (value: string) => {
      const cleanedValue = value.replace(SWIFT_CODE_REGEX, "");
      updateField(field, cleanedValue);
    },
    [updateField]
  );

  /**
   * Set entire form state (useful for initialization)
   */
  const setFormData = useCallback((data: Partial<BankFormState>) => {
    setFormState((prev) => ({ ...prev, ...data }));
  }, []);

  return {
    formState,
    errors,
    setAllErrors,
    clearError,
    updateField,
    setFormData,
    createChangeHandler,
    createNumericChangeHandler,
    createSwiftCodeChangeHandler,
  };
};
